import { MetricCard } from "design-system/organisms/insights/MetricCard"
import { formatNumber, formatPercent } from "lopermut"
import {
  useActionRequestHoursSavedMetricQuery,
  useCustomersMetricQuery,
  useDeflectionRateMetricQuery,
  useHoursSavedMetricQuery,
  useIssueDeflectionRateMetricQuery,
  useNpsMetricQuery,
  useQuestionHoursSavedMetricQuery,
} from "modules/dashboard/hooks/useMetricsQueries"
import { useTranslation } from "react-i18next"
import { GetMetricSchema, TimeFrame } from "schema"
import { z } from "zod"
import { NetPromoterScore } from "../../../shared/components/Scores/NetPromoterScore"

type Props = {
  timeFrame: TimeFrame
  filters: Omit<z.input<typeof GetMetricSchema>, "timeFrame">
  activeMetricIdx: number
  setActiveMetricIdx: (idx: number) => void
  onHoverIdx: (idx: number) => void
  npsMetricIdx: number
  legacyContainmentMetricIdx: number | null
  legacyTimeSavedMetricIdx: number | null
  shouldShowNewSupport: boolean
  issueDeflectionMetricIdx: number
  supportEntityHoursSavedMetricIdx: number
}

export const DashboardMetrics = ({
  timeFrame,
  filters,
  activeMetricIdx,
  setActiveMetricIdx,
  onHoverIdx,
  npsMetricIdx,
  legacyContainmentMetricIdx,
  legacyTimeSavedMetricIdx,
  shouldShowNewSupport,
  issueDeflectionMetricIdx,
  supportEntityHoursSavedMetricIdx,
}: Props) => {
  const { t } = useTranslation()
  const deltaTooltip = t("dashboard.metrics.deltaTooltip")
  const query = { ...filters, timeFrame }

  const { data: customers, isPending: isCustomersPending } = useCustomersMetricQuery({ query })
  const { data: deflectionRate, isPending: isDeflectionPending } = useDeflectionRateMetricQuery({
    query,
    enabled: legacyContainmentMetricIdx !== null,
  })
  const { data: hoursSaved, isPending: isHoursPending } = useHoursSavedMetricQuery({
    query,
    enabled: legacyTimeSavedMetricIdx !== null,
  })
  const { data: nps, isPending: isNpsPending } = useNpsMetricQuery({ query })
  const { data: issueDeflectionRate, isPending: isIssueDeflectionPending } =
    useIssueDeflectionRateMetricQuery({ query, enabled: shouldShowNewSupport })
  const { data: questionHoursSaved, isPending: isQuestionHoursPending } =
    useQuestionHoursSavedMetricQuery({ query, enabled: shouldShowNewSupport })
  const { data: actionRequestHoursSaved, isPending: isActionRequestHoursPending } =
    useActionRequestHoursSavedMetricQuery({ query, enabled: shouldShowNewSupport })
  const supportEntityHoursSavedCurrent = sumOptionalValues(
    questionHoursSaved?.current,
    actionRequestHoursSaved?.current,
  )
  const supportEntityHoursSavedPrevious = sumOptionalValues(
    questionHoursSaved?.previous,
    actionRequestHoursSaved?.previous,
  )
  const supportEntityHoursSavedDelta =
    supportEntityHoursSavedPrevious && supportEntityHoursSavedCurrent !== undefined
      ? (supportEntityHoursSavedCurrent - supportEntityHoursSavedPrevious) /
        supportEntityHoursSavedPrevious
      : undefined

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 w-full">
      <MetricCard
        title={t("dashboard.metrics.customersImpacted.title")}
        tooltip={t("dashboard.metrics.customersImpacted.tooltip")}
        deltaTooltip={deltaTooltip}
        currentValue={formatNumber({
          amount: customers?.impacted.current,
          abvLevel: 3,
        })}
        deltaPercentage={customers?.impacted.deltaPercentage}
        isPending={isCustomersPending}
        className={`${activeMetricIdx === 0 ? "bg-accent" : ""}`}
        onClick={() => setActiveMetricIdx(0)}
        onMouseEnter={() => onHoverIdx(0)}
        onFocus={() => onHoverIdx(0)}
      />
      {legacyContainmentMetricIdx !== null && (
        <MetricCard
          title={t("dashboard.metrics.containmentRate.title")}
          tooltip={t("dashboard.metrics.containmentRate.tooltip")}
          deltaTooltip={deltaTooltip}
          currentValue={formatPercent({
            amount:
              deflectionRate?.current !== undefined ? deflectionRate.current * 100 : undefined,
            fix: 0,
          })}
          deltaPercentage={deflectionRate?.deltaPercentage}
          isPending={isDeflectionPending}
          className={`${activeMetricIdx === legacyContainmentMetricIdx ? "bg-accent" : ""}`}
          onClick={() => setActiveMetricIdx(legacyContainmentMetricIdx)}
          onMouseEnter={() => onHoverIdx(legacyContainmentMetricIdx)}
          onFocus={() => onHoverIdx(legacyContainmentMetricIdx)}
        />
      )}
      {legacyTimeSavedMetricIdx !== null && (
        <MetricCard
          title={t("dashboard.metrics.timeSaved.title")}
          tooltip={t("dashboard.metrics.timeSaved.tooltip")}
          deltaTooltip={deltaTooltip}
          currentValue={`${formatNumber({
            amount: hoursSaved?.current,
            abvLevel: 3,
            fix: 0,
          })}h`}
          deltaPercentage={hoursSaved?.deltaPercentage}
          isPending={isHoursPending}
          className={`${activeMetricIdx === legacyTimeSavedMetricIdx ? "bg-accent" : ""}`}
          onClick={() => setActiveMetricIdx(legacyTimeSavedMetricIdx)}
          onMouseEnter={() => onHoverIdx(legacyTimeSavedMetricIdx)}
          onFocus={() => onHoverIdx(legacyTimeSavedMetricIdx)}
        />
      )}
      {shouldShowNewSupport && (
        <>
          <MetricCard
            title={t("dashboard.metrics.issueDeflectionRate.title")}
            tooltip={t("dashboard.metrics.issueDeflectionRate.tooltip")}
            deltaTooltip={deltaTooltip}
            currentValue={formatPercent({
              amount:
                issueDeflectionRate?.current !== undefined
                  ? issueDeflectionRate.current * 100
                  : undefined,
              fix: 0,
            })}
            deltaPercentage={issueDeflectionRate?.deltaPercentage}
            isPending={isIssueDeflectionPending}
            className={`${activeMetricIdx === issueDeflectionMetricIdx ? "bg-accent" : ""}`}
            onClick={() => setActiveMetricIdx(issueDeflectionMetricIdx)}
            onMouseEnter={() => onHoverIdx(issueDeflectionMetricIdx)}
            onFocus={() => onHoverIdx(issueDeflectionMetricIdx)}
          />
          <MetricCard
            title={t("dashboard.metrics.supportEntityHoursSaved.title")}
            tooltip={t("dashboard.metrics.supportEntityHoursSaved.tooltip")}
            deltaTooltip={deltaTooltip}
            currentValue={`${formatNumber({
              amount: supportEntityHoursSavedCurrent,
              abvLevel: 3,
              fix: 0,
            })}h`}
            deltaPercentage={supportEntityHoursSavedDelta}
            isPending={isQuestionHoursPending || isActionRequestHoursPending}
            className={activeMetricIdx === supportEntityHoursSavedMetricIdx ? "bg-accent" : ""}
            onClick={() => setActiveMetricIdx(supportEntityHoursSavedMetricIdx)}
            onMouseEnter={() => onHoverIdx(supportEntityHoursSavedMetricIdx)}
            onFocus={() => onHoverIdx(supportEntityHoursSavedMetricIdx)}
          />
        </>
      )}
      <MetricCard
        title={t("dashboard.metrics.nps.title")}
        tooltip={t("dashboard.metrics.nps.tooltip")}
        deltaTooltip={deltaTooltip}
        currentValue={formatNumber({
          amount: nps?.current,
          fix: 0,
        })}
        deltaPercentage={nps?.deltaPercentage}
        isPending={isNpsPending}
        className={`${activeMetricIdx === npsMetricIdx ? "bg-accent" : ""}`}
        onClick={() => setActiveMetricIdx(npsMetricIdx)}
        onMouseEnter={() => onHoverIdx(npsMetricIdx)}
        onFocus={() => onHoverIdx(npsMetricIdx)}
      >
        <NetPromoterScore nps={nps?.current} isPending={isNpsPending} />
      </MetricCard>
    </div>
  )
}

function sumOptionalValues(...values: Array<number | undefined>) {
  if (values.every((value) => value === undefined)) return undefined

  return values.reduce<number>((total, value) => total + (value ?? 0), 0)
}
